import React, { forwardRef } from "react";

import { pressPoint } from "./glass";
import renderIcon from "./renderIcon";

const cx = (...a) => a.filter(Boolean).join(" ");

/**
 * TagUI — singura etichetă a aplicației: o stare sau o valoare scurtă. Înlocuiește
 * insignele (Badge: adminReviewsPopup__badge, exam-admin__badge, shome-lesson__badge,
 * wd-status…), numărul de necitite din colțul unui buton (CountBadge: menu__badge,
 * studentMobileTopBar__badge, profileTabs__badge), chip-urile de filtru și filele-pastilă
 * (ChoicePill: auditLog__chip, messageTemplates__chip / __tab), starea examenului,
 * scorurile de pe plăcuțele colorate și punctul de legendă. Stilurile: scss/UI/_tagUI.scss.
 *
 * tone: culoarea — neutral (black-s, implicit) | muted (black-s, text stins) |
 *       page (black-p: pe un panou black-s) | accent | success (green-l) | danger (red-l) |
 *       danger-soft (red-t) | warning (orange-t / orange-d) | info (blue-t / blue-d) |
 *       on-color (pe o plăcuță colorată: fundal --blur-bg, textul plăcuței, cifre tabulare)
 * size: sm (implicit: insignele din admin, 4×8, 12px) | md (insignele elevului, 6×10,
 *       12px / 600) | lg (fila-pastilă cu număr, messageTemplates__tab: 10×16, 14px / 600)
 * icon: nume UIIcon / svg importat / element (13px, culoarea textului)
 * count: numărul de după text (12px, stins, ca messageTemplates__tabCount)
 * dot: punctul de 8px dinaintea textului — legenda graficelor, „Live”. Eticheta rămâne
 *      fără fundal, cu textul și mărimea locului (ca legenda de azi); punctul ia culoarea
 *      din tone (success → verde…) sau din dotColor (orice culoare CSS) · pulse: punctul pulsează
 * onClick: devine <button> — chip-ul de filtru; selected: ales (aria-pressed, accent);
 *          disabled. Cu role propriu (de ex. "tab" + aria-selected) nu mai pune aria-pressed.
 * corner: numărul de necitite din colțul de sus-dreapta al părintelui (părintele are
 *         position: relative): 18px, accent, inel de 2px în culoarea fundalului de dedesubt
 *         (--tagUI-ring, implicit black-s). Peste `max` (99) scrie „99+”; ≤ 0 nu apare;
 *         `dot` fără număr = doar punctul. Locul: --tagUI-top / --tagUI-right (8px).
 */
const TagUI = forwardRef(function TagUI(
   {
      tone,
      size = "sm",
      icon,
      count,
      max = 99,
      dot = false,
      dotColor,
      pulse = false,
      corner = false,
      onClick,
      selected,
      disabled = false,
      className = "",
      style,
      type,
      children,
      ...rest
   },
   ref,
) {
   // ── numărul din colț (fostul CountBadge) ──
   if (corner) {
      const n = Number(count);
      const hasCount = n > 0;
      if (!hasCount && !dot) return null;
      const t = tone || "accent";
      return (
         <span
            ref={ref}
            className={cx("tagUI", "tagUI--corner", t !== "accent" && `tagUI--${t}`, !hasCount && "tagUI--cornerDot", className)}
            style={style}
            aria-hidden="true"
            {...rest}
         >
            {hasCount ? (n > max ? `${max}+` : n) : null}
         </span>
      );
   }

   const t = tone || "neutral";
   const isButton = typeof onClick === "function";
   const Component = isButton ? "button" : "span";
   const hasCount = count !== undefined && count !== null && count !== false;

   return (
      <Component
         ref={ref}
         type={isButton ? type || "button" : undefined}
         className={cx(
            "tagUI",
            t !== "neutral" && `tagUI--${t}`,
            size !== "sm" && `tagUI--${size}`,
            dot && "tagUI--dot",
            isButton && "tagUI--button",
            selected && "is-selected",
            className,
         )}
         style={dotColor ? { "--tagUI-dot": dotColor, ...style } : style}
         onClick={isButton ? onClick : undefined}
         onPointerDown={isButton ? pressPoint : undefined}
         disabled={isButton ? disabled : undefined}
         aria-disabled={!isButton && disabled ? true : undefined}
         aria-pressed={isButton && !rest.role && selected !== undefined ? Boolean(selected) : undefined}
         {...rest}
      >
         {dot && <span className={cx("tagUI__dot", pulse && "tagUI__dot--pulse")} aria-hidden="true" />}
         {renderIcon(icon, "tagUI__icon")}
         {children}
         {hasCount && <span className="tagUI__count">{count}</span>}
      </Component>
   );
});

/**
 * TagUI.Group — rândul de etichete sau de chip-uri (auditLog__chips,
 * messageTemplates__chips / __tabs, adminReviewsPopup__badges, wd-tags): 6px între
 * ele, se rup pe rânduri, role="group" (dă-i aria-label când sunt chip-uri).
 * legend: rândul unei legende (practice-stats__legend, wd-group__legend,
 *         wd-journey__legend): 6×16 între intrări, text 12px stins, cifre tabulare.
 */
const TagGroup = forwardRef(function TagGroup({ legend = false, className = "", children, ...rest }, ref) {
   return (
      <div ref={ref} role={legend ? undefined : "group"} className={cx("tagUIGroup", legend && "tagUIGroup--legend", className)} {...rest}>
         {children}
      </div>
   );
});

TagUI.Group = TagGroup;

export default TagUI;
